<template>
    <section class="help">
        <header class="help__header">
            <h1>Как агентству работать в 2digital DOOH</h1>
            <p>
                Пошаговая инструкция для агентств: доступ к платформе, структура «Рекламодатель — Бренд — Кампания»,
                создание рекламодателей и брендов, выдача доступов и настройка маркапа.
            </p>
        </header>

        <section class="help__section" id="access">
            <h2 class="help__section-title">
                <span class="num">1</span>
                Получение доступа к платформе
            </h2>
            <p class="help__section-desc">
                Учётную запись агентства создаёт администратор 2digital — самостоятельная регистрация на платформе не предусмотрена.
            </p>

            <ol class="help__steps">
                <li class="step">
                    <div class="step__num">1</div>
                    <div class="step__body">
                        <h3 class="step__title">Передайте контактные данные ответственного за агентство</h3>
                        <div class="step__content">
                            <p>
                                Email и ФИО сотрудника, который будет администрировать аккаунт агентства,
                                направляются менеджеру 2digital.
                            </p>
                        </div>
                    </div>
                </li>
                <li class="step">
                    <div class="step__num">2</div>
                    <div class="step__body">
                        <h3 class="step__title">Администратор создаёт учётную запись</h3>
                        <div class="step__content">
                            <p>
                                Аккаунту присваивается роль <b>Партнёр / Агентство</b> — это верхний уровень структуры,
                                внутри которого агентство далее создаёт рекламодателей.
                            </p>
                        </div>
                    </div>
                </li>
                <li class="step">
                    <div class="step__num">3</div>
                    <div class="step__body">
                        <h3 class="step__title">Получите логин и пароль</h3>
                        <div class="step__content">
                            <p>Логин и временный пароль присылает администратор 2digital на указанную почту.</p>
                            <div class="note">При первом входе рекомендуется сразу сменить временный пароль.</div>
                        </div>
                    </div>
                </li>
                <li class="step">
                    <div class="step__num">4</div>
                    <div class="step__body">
                        <h3 class="step__title">Войдите в платформу</h3>
                        <div class="step__content">
                            <p>
                                После входа откройте раздел
                                <a href="/administration">Администрирование</a>
                                через иконку профиля в правом верхнем углу. У агентства там вкладки
                                «Структура», «Пользователи» и «Калькулятор»: структура и наценки, доступы клиентов
                                и расчёт бюджета.
                            </p>
                        </div>
                    </div>
                </li>
            </ol>
        </section>

        <section class="help__section" id="structure">
            <h2 class="help__section-title">
                <span class="num">2</span>
                Структура «Рекламодатель — Бренд — Кампания»
            </h2>
            <p class="help__section-desc">
                Вся работа в платформе строится на трёхуровневой иерархии. Понимание структуры важно перед тем,
                как заводить первую кампанию.
            </p>

            <div class="breadcrumb-diagram">
                <div class="node">Агентство (Партнёр)<small>ваш аккаунт</small></div>
                <span class="arrow">→</span>
                <div class="node">Рекламодатель<small>клиент агентства</small></div>
                <span class="arrow">→</span>
                <div class="node">Бренд<small>продукт/линейка клиента</small></div>
                <span class="arrow">→</span>
                <div class="node">Кампания<small>конкретный запуск рекламы</small></div>
            </div>

            <ul class="help__plain-list">
                <li>
                    <b>Агентство (Партнёр)</b> — верхний уровень, ваш аккаунт в системе.
                </li>
                <li>
                    <b>Рекламодатель</b> — конечный клиент, которого агентство ведёт
                    (например, торговая сеть или бренд-владелец).
                </li>
                <li>
                    <b>Бренд</b> — конкретный бренд/продукт внутри рекламодателя, на который заводятся креативы и кампании.
                </li>
                <li>
                    <b>Кампания</b> — RTB-кампания с экранами, датами, лимитами и креативами; всегда привязана
                    к рекламодателю и бренду.
                </li>
            </ul>

            <div class="tip">
                Прежде чем создавать кампанию, нужно, чтобы у неё уже были рекламодатель и бренд — иначе выбрать
                их при создании кампании не получится (см. шаг 3).
            </div>
        </section>

        <section class="help__section" id="create-advertiser">
            <h2 class="help__section-title">
                <span class="num">3</span>
                Создание новых рекламодателей и брендов
            </h2>
            <p class="help__section-desc">
                Рекламодателей и бренды агентство заводит самостоятельно в разделе
                <a href="/administration">Администрирование → Структура</a>.
            </p>

            <ol class="help__steps">
                <li class="step">
                    <div class="step__num">1</div>
                    <div class="step__body">
                        <h3 class="step__title">Создайте рекламодателя</h3>
                        <div class="step__content">
                            <p>
                                Администрирование → Структура → у строки партнёра откройте «Действия» и выберите
                                «Добавить рекламодателя».
                            </p>
                            <p>
                                Справа откроется панель «Новый рекламодатель». Укажите название.
                                Партнёр уже выбран; если партнёров несколько, его можно сменить в том же поле.
                            </p>
                        </div>
                    </div>
                </li>
                <li class="step">
                    <div class="step__num">2</div>
                    <div class="step__body">
                        <h3 class="step__title">Добавьте бренд внутри рекламодателя</h3>
                        <div class="step__content">
                            <p>
                                В той же вкладке «Структура» у нужного рекламодателя откройте «Действия» и выберите
                                «Добавить бренд».
                            </p>
                            <p>
                                В панели «Новый бренд» рекламодатель уже выбран. Укажите название и, по желанию, описание.
                            </p>
                        </div>
                    </div>
                </li>
                <li class="step">
                    <div class="step__num">3</div>
                    <div class="step__body">
                        <h3 class="step__title">Проверьте созданную структуру</h3>
                        <div class="step__content">
                            <p>
                                Рекламодатель и бренд(ы) появятся в дереве структуры и будут доступны при создании
                                кампании и креатива. Один рекламодатель может содержать несколько брендов.
                                Рядом с названием показан иерархический номер вида <b>#…</b>.
                            </p>
                            <div class="note">
                                Название рекламодателя или бренда меняется в любой момент:
                                «Действия → Редактировать».
                            </div>
                        </div>
                    </div>
                </li>
            </ol>
        </section>

        <section class="help__section" id="access-types">
            <h2 class="help__section-title">
                <span class="num">4</span>
                Выдача доступов для рекламодателей
            </h2>
            <p class="help__section-desc">
                Если клиент (рекламодатель) должен сам заходить в платформу — например, смотреть статистику своих
                кампаний — агентство выдаёт ему отдельную учётную запись в разделе
                <a href="/administration">Администрирование → Пользователи</a>.
            </p>

            <table class="access-table">
                <thead>
                    <tr>
                        <th>Поле</th>
                        <th>Значение</th>
                        <th>Что означает</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td><b>Роль</b></td>
                        <td>User PRO (рекомендуется) / User Standard</td>
                        <td>Агентство может создавать пользователей с ролями User PRO и User Standard</td>
                    </tr>
                    <tr>
                        <td rowspan="2"><b>Режим доступа</b></td>
                        <td>Полный доступ</td>
                        <td>Пользователь может создавать и менять структуру, RTB-кампании, креативы</td>
                    </tr>
                    <tr>
                        <td>Только просмотр</td>
                        <td>Запрещает любые изменения: структуру, RTB-кампании, креативы</td>
                    </tr>
                    <tr>
                        <td><b>Рекламодатели</b></td>
                        <td>Список выбранных рекламодателей</td>
                        <td>Пользователь увидит только выбранных рекламодателей и их бренды/кампании</td>
                    </tr>
                </tbody>
            </table>

            <ol class="help__steps help__steps--spaced">
                <li class="step">
                    <div class="step__num">1</div>
                    <div class="step__body">
                        <h3 class="step__title">Откройте вкладку «Пользователи» и нажмите «Добавить пользователя»</h3>
                        <div class="step__content">
                            <p>
                                Администрирование → Пользователи → «+ Добавить пользователя».
                                Список фильтруется переключателями «Активные», «Ожидают» и «Неактивные»,
                                поиском по email и ролью. Фильтр по партнёру появляется, только если партнёров несколько.
                                Роль и режим уже созданного пользователя меняются через «Действия → Редактировать».
                            </p>
                        </div>
                    </div>
                </li>
                <li class="step">
                    <div class="step__num">2</div>
                    <div class="step__body">
                        <h3 class="step__title">Заполните форму создания пользователя</h3>
                        <div class="step__content">
                            <p>
                                Справа откроется панель создания. Укажите email и пароль: его можно сгенерировать,
                                показать и скопировать. Роль выберите переключателем — для клиента это
                                <b>User PRO</b> (рекомендуется) или <b>User Standard</b>. Режим доступа —
                                «Полный доступ» или «Только просмотр». Рекламодателей отметьте галочками:
                                в списке есть поиск и кнопки «Выбрать все» и «Снять выбор».
                            </p>
                            <div class="warn">
                                Проверяйте список выбранных рекламодателей внимательно: без ограничения по конкретным
                                рекламодателям пользователь может получить доступ шире, чем нужно.
                            </div>
                        </div>
                    </div>
                </li>
                <li class="step">
                    <div class="step__num">3</div>
                    <div class="step__body">
                        <h3 class="step__title">Передайте доступ клиенту</h3>
                        <div class="step__content">
                            <p>
                                Логин и пароль, указанные при создании пользователя, передайте клиенту любым
                                удобным способом.
                            </p>
                        </div>
                    </div>
                </li>
            </ol>
        </section>

        <section class="help__section" id="markup">
            <h2 class="help__section-title">
                <span class="num">5</span>
                Выставление маркапа
            </h2>
            <p class="help__section-desc">
                Наценка задаётся в разделе
                <a href="/administration">Администрирование → Структура</a>.
                Отдельной вкладки «Наценки» нет.
                Значение, заданное на уровне агентства (партнёра), по умолчанию применяется ко всем его RTB-кампаниям,
                но может быть переопределено для конкретной кампании.
            </p>

            <div class="breadcrumb-diagram">
                <div class="node">Наценка агентства<small>базовое значение</small></div>
                <span class="arrow">→</span>
                <div class="node">Override на рекламную кампанию<small>опционально, высший приоритет</small></div>
            </div>

            <ol class="help__steps">
                <li class="step">
                    <div class="step__num">1</div>
                    <div class="step__body">
                        <h3 class="step__title">Задайте базовую наценку агентства</h3>
                        <div class="step__content">
                            <p>
                                Администрирование → Структура → у партнёра нажмите чип с агентской наценкой (L2)
                                или откройте «Действия → Наценки». В панели «Наценки партнёра» укажите L2
                                и нажмите «Сохранить». Это значение применяется по умолчанию ко всем клиентам агентства.
                            </p>
                        </div>
                    </div>
                </li>
                <li class="step">
                    <div class="step__num">2</div>
                    <div class="step__body">
                        <h3 class="step__title">При необходимости переопределите наценку для рекламной кампании</h3>
                        <div class="step__content">
                            <p>
                                Раскройте рекламодателя в «Структуре» и нажмите чип у RTB-кампании.
                                Пока своя наценка не задана, на чипе написано «Наценка партнёра»; после переопределения
                                там виден процент. В панели «Наценка кампании» укажите % и сохраните —
                                он заменит наценку партнёра только у этой кампании.
                            </p>
                            <div class="note">
                                Если override для кампании не задан — действует базовая наценка агентства.
                                Кнопка «Как у партнёра» снимает переопределение.
                            </div>
                        </div>
                    </div>
                </li>
            </ol>
        </section>

        <section class="help__faq">
            <h2>Частые вопросы</h2>
            <details>
                <summary>Можно ли агентству создать рекламодателя без бренда?</summary>
                <p>
                    Рекламодателя создать можно, но кампанию завести нельзя, пока внутри него нет хотя бы одного
                    бренда — бренд обязателен для запуска кампании.
                </p>
            </details>
            <details>
                <summary>Клиент видит наценку агентства?</summary>
                <p>
                    Нет. Пользователь с доступом уровня рекламодателя видит бюджет и показатели только в своей части,
                    без наценки агентства и администратора платформы.
                </p>
            </details>
            <details>
                <summary>Что делать, если забыли пароль от учётной записи?</summary>
                <p>
                    Обратитесь на
                    <a href="mailto:sales@2digital.tech">sales@2digital.tech</a>
                    — восстановление доступа выполняется через эту почту.
                </p>
            </details>
        </section>
    </section>
</template>

<script>
export default {
    name: 'HelpAgency',
    props: {
        userData: {
            type: Object,
            default: () => ({}),
        },
    },
};
</script>

<style scoped>
/* Стили вынесены в resources/scss/pages/_help.scss и собираются через Mix */
</style>
